import type { NID } from '@navina/api-types';

interface PatientScopedRequest {
	readonly patientId: NID;
}

interface PatientScopedRequestWithOptionalAppointmentId extends PatientScopedRequest {
	readonly appointmentId?: string;
}

type PatientScopedApiMethod<RequestType extends PatientScopedRequest, ResponseType> = (
	request: RequestType,
) => Promise<ResponseType>;

type CachedPatientRequest<ResponseType> = {
	readonly patientId: NID;
	readonly appointmentId: string | undefined;
	readonly promise: Promise<ResponseType>;
} | null;

export function getMemoizedApiMethodByPatientIdOnce<
	RequestType extends PatientScopedRequestWithOptionalAppointmentId,
	ResponseType,
>(apiMethod: PatientScopedApiMethod<RequestType, ResponseType>): (request: RequestType) => Promise<ResponseType> {
	let cachedRequest: CachedPatientRequest<ResponseType> = null;

	return async function getOncePerPatientId(request: RequestType): Promise<ResponseType> {
		const requestAppointmentId = 'appointmentId' in request ? request.appointmentId : undefined;

		if (cachedRequest?.patientId === request.patientId && cachedRequest?.appointmentId === requestAppointmentId) {
			return cachedRequest.promise;
		}

		const promise = apiMethod(request);
		cachedRequest = {
			patientId: request.patientId,
			appointmentId: requestAppointmentId,
			promise,
		};

		return promise;
	};
}
